// Eine Tabellen-Zeile mit Avatar + Online-Dot + Tier-Pill + Status-Pills
// + Action-Menu (Hover-Reveal). Click auf Row öffnet Drawer.

import { Crown, Gem, Gift, MoreHorizontal, User } from 'lucide-react';
import type { AdminUserRow, UserTier } from '@/lib/admin-users-api';

const TIER_PILL: Record<UserTier, { label: string; tone: string; Icon: typeof Crown }> = {
  owner: { label: 'OWNER', tone: 'border-amber-500/40 bg-amber-500/15 text-amber-300', Icon: Crown },
  pro: { label: 'PRO', tone: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300', Icon: Gem },
  trial: { label: 'TRIAL', tone: 'border-sky-500/40 bg-sky-500/15 text-sky-300', Icon: Gift },
  free: { label: 'FREE', tone: 'border-white/10 bg-white/[0.04] text-neutral-300', Icon: User },
};

interface UserRowProps {
  user: AdminUserRow;
  onSelect: (user: AdminUserRow) => void;
  now: Date;
}

export function UserRow({ user, onSelect, now }: UserRowProps) {
  const tier = TIER_PILL[user.tier];
  const online = isOnline(user.last_seen, now);
  const initials = (user.nickname ?? user.email).slice(0, 2).toUpperCase();
  return (
    <tr
      onClick={() => onSelect(user)}
      className="group cursor-pointer border-t border-white/5 transition-colors hover:bg-amber-500/[0.03]"
    >
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <span
            className={`size-1.5 rounded-full ${online ? 'animate-pulse bg-emerald-400' : 'bg-neutral-700'}`}
            title={online ? 'Online' : 'Offline'}
          />
          <div className="grid size-7 place-items-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-[10px] font-bold text-neutral-950">
            {initials}
          </div>
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="font-mono text-[12px] text-neutral-100">{user.email}</div>
        {user.nickname ? (
          <div className="text-[10px] text-neutral-500">{user.nickname}</div>
        ) : null}
      </td>
      <td className="px-3 py-2">
        <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[10px] font-bold ${tier.tone}`}>
          <tier.Icon className="size-3" />
          {tier.label}
        </span>
      </td>
      <td className="px-3 py-2 text-[12px]">
        <CheckIcon ok={!!user.disclaimer_accepted_at} title="Disclaimer" />
        <CheckIcon ok={!!user.privacy_accepted_at} title="Datenschutz" />
      </td>
      <td className="px-3 py-2 text-[11px] text-neutral-400 tabular">
        {formatDaysSince(user.created_at, now)}
      </td>
      <td className="px-3 py-2 text-[11px] text-neutral-400 tabular">
        {formatRelativeFromNow(user.last_seen, now)}
      </td>
      <td className="px-3 py-2">
        {user.stripe_status === 'active' ? (
          <span className="rounded border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-300">
            active
          </span>
        ) : (
          <span className="text-[10px] text-neutral-600">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <span className="font-mono text-[11px] tabular text-amber-300">
          {user.balance.toLocaleString('de-DE')}
        </span>
      </td>
      <td className="px-3 py-2 text-right">
        <MoreHorizontal className="ml-auto size-4 text-neutral-600 opacity-0 transition-opacity group-hover:opacity-100" />
      </td>
    </tr>
  );
}

function CheckIcon({ ok, title }: { ok: boolean; title: string }) {
  return (
    <span
      className={`mr-1 inline-block size-2 rounded-full ${ok ? 'bg-emerald-400' : 'bg-rose-500/60'}`}
      title={`${title}: ${ok ? 'ja' : 'nein'}`}
    />
  );
}

function isOnline(lastSeen: string | null, now: Date): boolean {
  if (!lastSeen) return false;
  const ts = Date.parse(lastSeen);
  if (!Number.isFinite(ts)) return false;
  return now.getTime() - ts < 5 * 60_000;
}

function formatDaysSince(iso: string | null, now: Date): string {
  if (!iso) return '—';
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return '—';
  const days = Math.floor((now.getTime() - ts) / 86_400_000);
  return `${days}d`;
}

function formatRelativeFromNow(iso: string | null, now: Date): string {
  if (!iso) return 'nie';
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return 'nie';
  const diff = now.getTime() - ts;
  if (diff < 5 * 60_000) return 'jetzt';
  if (diff < 60 * 60_000) return `vor ${Math.floor(diff / 60_000)}m`;
  if (diff < 24 * 3600_000) return `vor ${Math.floor(diff / 3_600_000)}h`;
  return `vor ${Math.floor(diff / 86_400_000)}d`;
}
